import { SelectionModel } from '@angular/cdk/collections';
import { TitleCasePipe } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSort } from '@angular/material/sort';
import { SnakeCaseToHumanPipe } from '@pipes/snake-case-to-human.pipe';
import { TableCellPipe } from '@pipes/table-cell.pipe';
import { RavIcon, RavIconOutlined } from '@shared/icon/icon';
import { EditorService } from '@shared/services/editor.service';
import { FormatService } from '@shared/services/format.service';
import { MobileService } from '@shared/services/mobile.service';
import { TableColumn, TableConfig, TableSort } from '@shared/types/table';
import { Observable, Subscription } from 'rxjs';
import { TableAction } from '../actions/action';

@Component({
  selector: 'rav-mobile-table',
  templateUrl: './mobile-table.component.html',
  styleUrls: ['./mobile-table.component.scss']
})
export class MobileTableComponent implements OnInit, OnDestroy {
  @Input() actions: TableAction[];
  @Input() columnsToDisplay: string[];
  @Input() config: TableConfig;
  @Input() dataRows: any[];
  @Input() masterToggleStatus: 'checked' | 'unchecked' | 'indeterminate';
  @Input() processingDictionary: any = {};
  @Input() selectionChanges: Observable<SelectionModel<any>>;
  @Input() sortChanges: Observable<MatSort>;
  @Output() addClick = new EventEmitter<string>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() toggleClick = new EventEmitter<'select all' | 'deselect all'>();
  @Output() rowOptionsButtonClick = new EventEmitter<{ event: CustomEvent<any>; row: any }>();
  @Output() selectionMade = new EventEmitter<{ event: CustomEvent<any>; rowIndex: number }>();
  @Output() sortUpdate = new EventEmitter<TableSort>();
  @ViewChild('sortingMenuTrigger') sortingMenuTrigger: MatMenuTrigger;
  RavIcon = RavIcon;
  RavIconOutlined = RavIconOutlined;
  currentSelection: SelectionModel<any>;
  currentSort: MatSort;
  isSelectingItems = false;
  renamingRowId: string;
  private subscriptions: Subscription[] = [];

  constructor(
    private editor: EditorService,
    private mobile: MobileService,
    private snakeCaseToHumanPipe: SnakeCaseToHumanPipe,
    private tableCellPipe: TableCellPipe,
    private titlecasePipe: TitleCasePipe,
    public format: FormatService
  ) {}

  /**
   * Listens for window resize events and closes sorting menu if user leaves mobile view.
   */
  @HostListener('window:resize', ['$event'])
  onResize() {
    if (!this.mobile.isMobileDisplay()) {
      try {
        this.sortingMenuTrigger.closeMenu();
      } catch {}
    }
  }

  ngOnInit() {
    this.subscriptions.push(
      // Listens for selection changes and updates current selection
      this.selectionChanges.subscribe((selection) => {
        if (!this.mobile.isMobileDisplay()) this.isSelectingItems = selection.hasValue() ? true : false;
        this.currentSelection = selection;
      }),
      // Listens for sorting changes and updates current sort
      this.sortChanges.subscribe((sort) => {
        this.currentSort = sort;
      }),
      // Listens for editor state changes and updates renamingRowId
      this.editor.editorState$.subscribe(({ id, open }) => {
        this.renamingRowId = undefined;
        if (id && open) this.renamingRowId = id;
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  /**
   * Emits an event indicating a row's options button was clicked.
   *
   * @param event click or right click event
   * @param row row where user clicked
   * @param rightClick whether calling from right click event or not
   */
  emitRowOptionsButtonClick(event: CustomEvent, row: any, rightClick?: boolean) {
    if (this.columnsToDisplay.includes('mobileAction')) this.rowOptionsButtonClick.emit({ event, row });
    if (rightClick) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * Emits a select or deselect event for a row.
   *
   * @param event click event
   * @param rowIndex index of the selected or deselected row
   */
  emitSelectionMade(event: CustomEvent, rowIndex: number) {
    this.selectionMade.emit({ event, rowIndex });
  }

  /**
   * Emits an event indicating that the user wants to sort the table by clicking sorting menu.
   *
   * @param columnKey key of the column by which user wants to sort the table
   */
  emitSortUpdate(columnKey: string) {
    const newSort: TableSort = {
      key: columnKey,
      // If the user selected a column to sort the table other than the current one, ascending is default.
      // Otherwise, update asc value.
      asc: this.currentSort.active !== columnKey ? true : this.currentSort.direction === 'asc' ? false : true
    };
    this.sortUpdate.emit(newSort);
  }

  /**
   * Returns a string with the values of the title keys separated by spaces
   * for a specific row.
   *
   * @param row row for which you want to get the title
   * @returns title keys column values separated by spaces
   */
  getRowTitle(row: any): string {
    let title = '';
    if (this.config.mobileSettings) {
      for (const titleKey of this.config.mobileSettings.titleKeys) {
        title = `${title} ${row[titleKey]}`;
      }
    }
    return title;
  }

  /**
   * Returns a string with the subtitle for a specific row considering current
   * sorting and row additional details.
   *
   * @param row row for which you want to get the subtitle
   * @returns subtitle as follows: "header: details"
   */
  getRowSubtitle(row: any): string {
    let header = '';
    let details = '';
    // Only get value from sorting if it is available and different from title value
    // Otherwise get subtitle details from config
    if (this.columnFromCurrentSort && this.columnFromCurrentSort.key !== this.config.mobileSettings.titleKeys[0]) {
      header += this.columnFromCurrentSort.header ? this.columnFromCurrentSort.header : this.columnFromCurrentSort.key;
      // Display additional details if sorting key is the same as config subtitle key
      // Otherwise display details from sorting
      details +=
        this.config.mobileSettings.subtitleAdditionalInfo &&
        this.columnFromCurrentSort.key === this.config.mobileSettings.subtitleKey
          ? this.formatDestinationRecipient(row)
          : this.tableCellPipe.transform(row[this.columnFromCurrentSort.key], this.columnFromCurrentSort.key);
    } else {
      header += this.config.mobileSettings.subtitleHeader;
      // Display additional details if available
      // Otherwise display row details according to config subtitle key
      details += this.config.mobileSettings.subtitleAdditionalInfo
        ? this.formatDestinationRecipient(row)
        : this.tableCellPipe.transform(
            row[this.config.mobileSettings.subtitleKey],
            this.config.mobileSettings.subtitleKey
          );
    }
    header = this.titlecasePipe.transform(header);
    header = this.snakeCaseToHumanPipe.transform(header);
    if (details === 'null') details = '';
    return `${header}: ${details}`;
  }

  /**
   * Formats destination recipient using current config so it shows
   * the folder name instead of gdrive, for example.
   */
  formatDestinationRecipient(row: any): string {
    return this.format.formatDestinationRecipient(
      row[this.config.mobileSettings.subtitleKey],
      row[this.config.mobileSettings.subtitleAdditionalInfo]
    );
  }

  /**
   * Whether a row is selected or not by its index number.
   */
  isRowSelected(rowIndex: number): boolean {
    if (this.currentSelection && this.currentSelection.isSelected(this.dataRows[rowIndex])) return true;
    return false;
  }

  /**
   * Handles the click events of the checkbox at the top of the table. Allows to activate
   * the selection mode and ask the parent component to select or deselect all items.
   *
   * @param event CustomEvent from checkbox click
   */
  toggleIsSelectingItems(event: CustomEvent) {
    if (this.isSelectingItems) {
      this.toggleClick.emit();
    } else {
      this.isSelectingItems = true;
      this.toggleClick.emit('deselect all');
    }
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Looks in the columns of the current table configuration, the table by which the data is being sorted.
   */
  get columnFromCurrentSort(): TableColumn {
    if (this.currentSort) return this.config.columns.find((column) => column.key === this.currentSort.active);
  }

  /**
   * Updates the file name according to the event's attributes. The event is emitted in the EditorComponent.
   * The element to be renamed will always be the one at index 0 of the title keys. See TableConfig.mobileSettings
   * in table.ts file.
   */
  updateFileName(event: any) {
    if (event.text && event.id && event.updated) {
      const renamedRow = this.dataRows.find((row) => row.id === event.id);
      if (renamedRow) {
        renamedRow[this.config.mobileSettings.titleKeys[0]] = event.text;
        if (renamedRow['updated_at']) renamedRow['updated_at'] = event.updated.toISOString();
        if (!isNaN(renamedRow['version'])) {
          const versionArray = renamedRow['version'].split('.');
          renamedRow['version'] = `${versionArray[0]}.${+versionArray[1] + 1}`;
        }
      }
    }
  }

  /**
   * Looks for Create New or Add New action in table actions and returns it.
   */
  get createAction(): TableAction {
    let createAction: TableAction;
    if (this.actions) {
      createAction = this.actions.find((action) => action.name === 'Create New');
      if (!createAction) createAction = this.actions.find((action) => action.name === 'Add New');
    }
    return createAction;
  }

  shouldDisplaySortingOption(tableColumn: TableColumn): boolean {
    return tableColumn.key.toLowerCase() !== 'reset password';
  }

  typeOf(val: any) {
    return typeof val;
  }
}
